import translator from './Translator';

export default class TreeObject {
    // only the JSON root contains the parameter sort_children_by_title, so that only the root tree object can use it
    // the value of sortChildrenByTitle is than passed to child objects via constructor parameter
    constructor(api, jsonObject, parent, sortChildrenByTitle = false) {
        this.api = api;
        this.jsonObject = jsonObject;
        this.objParent = parent || null;
        this.objChildren = {
            uncategorized: [],
            categories: [],
        };
        this.uid = null;
        // if sort children parameter is set in JSON data then use it
        // the sort children parameter is only set in the JSON root object
        // therefore it must then be passed to the children
        if (typeof this.jsonObject.sort_children_by_title !== 'undefined') {
            this.sortChildrenByTitle = this.jsonObject.sort_children_by_title;
        } else {
            this.sortChildrenByTitle = sortChildrenByTitle;
        }

        // Whenever this tree object is instantiated, we load the children in advance.
        this.finishedLoading = false;
        const path = this.treePathString;

        // This promise can be reused
        this.finishedLoadingPromise = new Promise((resolve, reject) => {
            // Whenever this tree object is instantiated, we load the children in advance.
            // check children for default api only:
            // the initial tree path for structure navi
            // might have a final node with has_children == true and empty children list, which
            // we need to prevent from being fetched.
            // check has_children !== false to catch true and undefined
            if (this.api.apiType !== 'structure'
                && path
                && this.jsonObject.has_children !== false
                && this.jsonObject.children.length === 0) {
                this.api.getPathData(path).then((data) => {
                    // only overwrite the json structure if we received something
                    if (data) {
                        this.jsonObject = data;
                    }
                    this.finishedLoading = true;
                    resolve(this.jsonObject);
                });
            } else {
                this.finishedLoading = true;
                resolve(this.jsonObject);
            }
        });

        // Trigger loading
        this.finishedLoadingPromise.then();
    }

    /**
     * init is lazily called when access happens (so the children of the children will not be touched here)
     */
    initializeChildren() {
        // stop if we already have been initialized and (should have no children or already has child objects)
        // this allows reinitialization when more levels of data have been expanded
        // attention: has_children can be undefined (which counts as "not false" here)
        // TODO: add has_children to structure json

        // stop if this node is bogus
        if (!this.jsonObject || !this.jsonObject.id) {
            return;
        }
        if (this.jsonObject.has_children === false
            || (!this.jsonObject.force_reload
                && (this.objChildren.categories.length + this.objChildren.uncategorized.length))) {
            return;
        }

        if (!this.finishedLoading) {
            console.warn(`Accessing children before having loaded them for object ${this.treePathString}`);
        }

        // check has_children vs. children list and expand, if data is missing, using the api
        this.api.expandData(this);
    }

    /**
     * build the TreeObject structures from the json children
     */
    createChildren() {
        if (this.jsonObject.children) {
            this.jsonObject.children.forEach((childJSON) => {
                let child = childJSON;
                let childObject = this.checkForChild(child.id);
                if (childObject) {
                    return;
                }
                if (this.api.apiType !== 'structure') {
                    // TODO: if we repair the parent-child - refs in the pathMap in the set-method,
                    // this becomes obsolete
                    // recheck the api pathsMap with the child path to get a current representation
                    const childPath = child.url.slice(window.PORTAL_URL.length);
                    child = this.api.pathsMap.getData(childPath);
                }
                // Generate a new TreeObject from the child
                if (!child) {
                    return;
                }
                childObject = new TreeObject(this.api, child, this, this.sortChildrenByTitle);

                // Save the children and categories
                if (childObject.isCategory) {
                    this.objChildren.categories.push(childObject);
                } else {
                    this.objChildren.uncategorized.push(childObject);
                }
            });

            // Sort the uncategorized children by order attribute first and title second
            if (this.sortChildrenByTitle) {
                const { lang } = document.documentElement;
                this.objChildren.uncategorized.sort((a, b) => {
                    const order = a.order - b.order;
                    if (order !== 0) {
                        return order;
                    }
                    return a.title.localeCompare(b.title, lang);
                });
            }
        }
    }

    static translate(key) {
        // basic types untouched
        if (typeof key !== 'object') {
            return key;
        }

        // when key is an object with the languages as attributes, use those
        const translated = typeof translator.lang !== 'undefined' && key[translator.lang];
        return translated || key.de || key[Object.keys(key)[0]] || null;
    }

    /**
     * Returns the real parent as TreeObject (not the category)
     * @returns {TreeObject|null}
     */
    get parent() {
        if (this.objParent && this.objParent.isCategory) {
            return this.objParent.parent;
        }
        return this.objParent;
    }

    get active() { return ('active' in this.jsonObject) ? !!this.jsonObject.active : false; }

    get dynamic() { return ('dynamic' in this.jsonObject) ? this.jsonObject.dynamic : false; }

    get id() { return this.jsonObject.id; }

    get isCategory() { return ('category' in this.jsonObject) ? !!this.jsonObject.category : false; }

    get order() { return ('order' in this.jsonObject) ? this.jsonObject.order : 0; }

    get review_state() { return this.jsonObject.review_state; }

    get title() { return TreeObject.translate(this.jsonObject.title); }

    get url() {
        const url = TreeObject.translate(this.jsonObject.url);
        if (typeof url === 'string' && url.startsWith('/')) {
            return window.PORTAL_URL + url;
        }
        return url;
    }

    // Whether this node has children
    get hasChildren() {
        if (typeof this.jsonObject.has_children !== 'undefined') {
            return this.jsonObject.has_children;
        }
        if (typeof this.jsonObject.children !== 'undefined') {
            return this.jsonObject.children.length > 0;
        }
        return false;
    }

    get is_private() {
        let isPrivate = this.jsonObject.is_private;
        if (typeof isPrivate === 'undefined') {
            isPrivate = false;
        }
        return isPrivate;
    }

    get is_excluded_from_nav() {
        let isExcludedFromNav = this.jsonObject.is_excluded_from_nav;
        if (typeof isExcludedFromNav === 'undefined') {
            isExcludedFromNav = false;
        }
        return isExcludedFromNav;
    }

    // Children are all uncategorized children and categories
    get children() {
        this.initializeChildren();
        return this.objChildren.categories.concat(this.objChildren.uncategorized);
    }

    // Uncategorized children
    get uncategorizedChildren() {
        this.initializeChildren();
        return this.objChildren.uncategorized;
    }

    // Categories
    get categories() {
        this.initializeChildren();
        return this.objChildren.categories;
    }

    /**
     * Get a child (or categorized grand-child) by ID
     *
     * @param {string} childId - id of child
     */
    getChild(childId) {
        this.initializeChildren();
        const allChildren = this.objChildren.uncategorized.concat(
            ...this.objChildren.categories.map((cat) => cat.children),
        );
        return allChildren.find((child) => child.id === childId);
    }

    /**
     * Get a child (or categorized grand-child) by ID
     * checkForChild does not init the children
     * if the child is not there yet, checkForChild returns null
     * @param {String} childId
     * @returns {TreeObject|null}
     */
    checkForChild(childId) {
        try {
            const allChildren = this.objChildren.uncategorized.concat(
                ...this.objChildren.categories.map((cat) => cat.children),
            );
            return allChildren.find((child) => child.id === childId);
        } catch (e) {
            console.error(e);
        }
        return null;
    }

    /**
     * Get all objects on the path from root to this treeObject
     *
     * @returns {Array}
     */
    get treePath() {
        if (this.parent) {
            return this.parent.treePath.concat([this]);
        }

        return [this];
    }

    /**
     * Get tree path until root as string
     *
     * @returns {string}
     */
    get treePathString() {
        // only outside structure navi
        if (this.api.apiType !== 'structure') {
            return this.jsonObject.url?.replace(window.PORTAL_URL, '');
        }
        return '';
    }

    // Get all objects but categories on the path from root to this treeObject
    get breadcrumbs() {
        // treePath without categories
        return this.treePath.filter((treeObject) => !treeObject.isCategory);
    }

    /**
     * Check if given TreeObject is an ancestor of this object
     * @param {TreeObject} object
     * @returns {Boolean}
     */
    hasAncestor(object) {
        return this.breadcrumbs.includes(object);
    }

    /**
     * Check if this is an ancestor of a given TreeObject
     * @param {TreeObject} object
     * @returns {Bool}
     */
    isAncestorOf(object) {
        return object.hasAncestor(this);
    }

    // Get an unique ID of this object. Generate a new one, if no ID has previously been used.
    get uniqueID() {
        if (!this.uid) {
            this.uid = ((new Date().getTime()) + Math.random()).toString();
        }
        return this.uid;
    }

    /**
     * Returns the promise, that resolves, when the children have been loaded.
     * @returns {Promise}
     */
    childrenReady() {
        return this.finishedLoadingPromise;
    }
}
